import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import queryString from 'query-string';
import { Formik } from 'formik';

import extractPhoneFromText from 'common/util/extractPhoneFromText';
import { formatPhoneNumber } from 'common/formatters';
import isUserAgentMobileApp from 'common/util/isUserAgentMobileApp';

import { USER_ROLE_APARTMENT_LOCATOR, USER_ROLE_ADMIN } from 'common/userRoles';
import { SCREEN_ID_APARTMENT_LOCATOR_PHONE_CALL } from 'client/constants/screenIds';

import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import { createFormValidationFn } from 'client/helpers/formikHelpers';
import sunroomPhoneClient from 'client/lib/sunroomPhoneClient';

import Navbar from 'client/components/Navbar';
import ButtonDeprecated, { BUTTON_THEME_GREEN, BUTTON_THEME_RED } from 'client/components/ButtonDeprecated';
import H1 from 'client/components/H1';
import SrAgentDashboardTabBar from 'client/components/SrAgentDashboardTabBar';
import SrFormikField from 'client/components/SrFormikField';

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_APARTMENT_LOCATOR, USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_APARTMENT_LOCATOR_PHONE_CALL)
@branch({
  currentUser: ['currentUser'],
  userAgent: ['analytics', 'userAgent'],
  phoneDevice: ['apartmentLocator', 'phoneDevice'],
})
class AgentCallPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Call | Sunroom Rentals',
    section: 'Call',
  });

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    phoneDevice: PropTypes.shape({
      isReady: PropTypes.bool.isRequired,
      isConnected: PropTypes.bool.isRequired,
      number: PropTypes.string,
      errorMessage: PropTypes.string,
    }).isRequired,
  }

  componentDidMount() {
    const { userAgent } = this.props;
    const isMobileApp = isUserAgentMobileApp(userAgent);

    if (!isMobileApp) {
      this.initialize();
    }
  }

  componentWillUnmount() {
    const { phoneDevice } = this.props;

    if (phoneDevice.isReady && !phoneDevice.isConnected) {
      sunroomPhoneClient.destroy();
    }
  }

  initialize = () => {
    const { dispatch } = this.props;
    dispatch(sunroomPhoneClient.init);
  }

  _call = ({ userPhone }) => {
    sunroomPhoneClient.connect(userPhone);
  }

  _hangup = () => {
    sunroomPhoneClient.disconnect();
  }

  render() {
    const {
      phoneDevice,
      currentUser,
      match,
      location,
      userAgent,
    } = this.props;
    const isMobileApp = isUserAgentMobileApp(userAgent);
    const querySearch = queryString.parse(location.search);

    return (
      <div
        className={classnames(
          'page-agentDashboard phone',
          { calling: phoneDevice.isConnected },
        )}
      >
        <Navbar redirectLogoTo={getUserLandingPage({ userRoles: currentUser.roles })} />
        <SrAgentDashboardTabBar />

        {!isMobileApp && (
          <div className='contentContainer'>
            <Formik
              enableReinitialize
              initialValues={{
                userPhone: extractPhoneFromText(match.params.userPhone || querySearch.userPhone)
                  || phoneDevice.number
                  || '',
              }}
              onSubmit={this._call}
              validate={createFormValidationFn([{ id: 'userPhone', validations: ['phone'] }])}
              render={(formikBag) => {
                return (
                  <div>
                    <div className='header'>
                      <H1>{phoneDevice.isConnected ? 'Calling now!' : 'Start a phone call'}</H1>
                      <span className='color-grey weight-medium'>
                        {sunroomPhoneClient.getClockTime()}
                      </span>
                    </div>
                    <span className='subhead'>
                      <span className='color-grey'>Calling from Sunroom number </span>
                      <span className='color-grey nowrap'>
                        {formatPhoneNumber(currentUser.secondaryPhone)}
                      </span>
                    </span>
                    <div className='phoneInputContainer'>
                      <SrFormikField
                        formField={{
                          id: 'userPhone',
                          type: 'short_text',
                          value: formikBag.values.userPhone,
                          properties: {
                            autoFocus: true,
                            disabled: phoneDevice.isConnected || !phoneDevice.isReady,
                            className: (phoneDevice.isConnected || !phoneDevice.isReady) ? 'disabled' : '',
                            placeholder: '5121234567',
                            onChange: (event) => {
                              const phone = event.target.value.trim();
                              const isPhone = !!extractPhoneFromText(phone);
                              if (formikBag.values.userPhone.length === 10 && isPhone) {
                                return;
                              }
                              formikBag.setFieldValue(
                                'userPhone',
                                isPhone ? extractPhoneFromText(phone) : phone
                              );
                            },
                          },
                        }}
                        formikBag={formikBag}
                      />
                    </div>
                    <div
                      className={classnames(
                        'callButton',
                        { endCall: phoneDevice.isConnected },
                      )}
                    >
                      <ButtonDeprecated
                        className='call'
                        theme={phoneDevice.isConnected ? BUTTON_THEME_RED : BUTTON_THEME_GREEN}
                        onClick={phoneDevice.isConnected ? this._hangup : formikBag.handleSubmit}
                      >
                        {`${phoneDevice.isConnected ? 'end' : ''} call`}
                      </ButtonDeprecated>
                    </div>
                    <div className='loadingContainer'>
                      {!phoneDevice.isReady && !phoneDevice.errorMessage && (
                        <span>Connecting ...</span>
                      )}
                      {phoneDevice.errorMessage && (
                        <span className='color-red'>
                          {phoneDevice.errorMessage}
                          <br />
                          Please tell the Sunroom team.
                        </span>
                      )}
                    </div>
                  </div>
                );
              }}
            />
          </div>
        )}
      </div>
    );
  }
}

AgentCallPage.displayName = 'AgentCallPage';

export default AgentCallPage;
